import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, Download, FileText, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Bulk-import mg-per-vial variants across products via CSV.
 * Columns: sku, mg, price, in_stock, name (name is optional/eyeball).
 * Server behavior: skip rows for existing (sku, mg) — never overwrite. Skip
 * unknown SKUs silently and surface them in the result summary.
 */
export default function VariantsBulkImportDialog({ open, onOpenChange, onImported }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const doUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please choose a .csv file.");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/products/variants/bulk-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Imported: ${data.added} new variant${data.added === 1 ? "" : "s"}`);
      onImported?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) doUpload(f);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) doUpload(f);
  };

  const templateHref = `${BACKEND}/api/admin/products/variants/bulk-template.csv`;
  const exportHref = `${BACKEND}/api/admin/products/variants/export.csv`;

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setResult(null); }}>
      <DialogContent className="rounded-2xl max-w-xl bg-bone border-borderSubtle" data-testid="variants-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Import mg-variants (CSV)</DialogTitle>
        </DialogHeader>

        <div className="mb-3 flex items-center gap-3 text-xs flex-wrap">
          <a
            href={templateHref}
            className="inline-flex items-center gap-1.5 text-oxblood hover:text-charcoal underline underline-offset-2"
            data-testid="variants-template-link"
          >
            <Download size={12} /> Download CSV template
          </a>
          <span className="text-stone">·</span>
          <a
            href={exportHref}
            className="inline-flex items-center gap-1.5 text-oxblood hover:text-charcoal underline underline-offset-2"
            data-testid="variants-export-link"
            title="Download every product's current mg-variants in the same CSV format for round-trip editing"
          >
            <Download size={12} /> Export current variants
          </a>
          <span className="text-stone">·</span>
          <span className="text-stone">Existing (sku + mg) rows are skipped — never overwritten.</span>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={onPick}
          data-testid="variants-import-input"
        />
        <div
          onClick={() => !busy && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`p-8 rounded-2xl border border-dashed cursor-pointer text-center transition-colors ${
            dragOver ? "border-oxblood bg-white" : "border-borderSubtle bg-white hover:border-charcoal"
          } ${busy ? "opacity-70 cursor-wait" : ""}`}
          data-testid="variants-import-dropzone"
        >
          <FileText size={22} strokeWidth={1.5} className="mx-auto text-stone mb-3" />
          <p className="font-medium">
            {busy ? "Importing…" : "Drag & drop a CSV or click to browse"}
          </p>
          <p className="text-[11px] text-stone mt-1 font-mono uppercase tracking-widest">
            Columns: sku, mg, price, in_stock, name
          </p>
        </div>

        {result && (
          <div className="mt-4 border border-borderSubtle bg-white p-4 max-h-64 overflow-y-auto" data-testid="variants-import-result">
            <div className="flex items-center gap-4 text-sm flex-wrap">
              <span className="inline-flex items-center gap-2 text-oxblood">
                <CheckCircle2 size={14} strokeWidth={2} />
                <span className="font-mono" data-testid="variants-import-added-count">{result.added}</span> added
              </span>
              {result.skipped_existing?.length > 0 && (
                <>
                  <span className="text-stone">·</span>
                  <span className="inline-flex items-center gap-2 text-charcoal">
                    <AlertCircle size={14} strokeWidth={2} />
                    <span className="font-mono">{result.skipped_existing.length}</span> skipped (already exists)
                  </span>
                </>
              )}
              {result.skipped_unknown_sku?.length > 0 && (
                <>
                  <span className="text-stone">·</span>
                  <span className="inline-flex items-center gap-2 text-charcoal">
                    <XCircle size={14} strokeWidth={2} />
                    <span className="font-mono">{result.skipped_unknown_sku.length}</span> unknown SKU
                  </span>
                </>
              )}
              {result.errors?.length > 0 && (
                <>
                  <span className="text-stone">·</span>
                  <span className="inline-flex items-center gap-2 text-oxblood">
                    <XCircle size={14} strokeWidth={2} />
                    <span className="font-mono">{result.errors.length}</span> errors
                  </span>
                </>
              )}
            </div>

            {result.skipped_existing?.length > 0 && (
              <div className="mt-3">
                <p className="text-[10px] uppercase tracking-widest font-mono text-stone mb-1">Skipped — already exists</p>
                <div className="space-y-1 max-h-24 overflow-y-auto" data-testid="variants-skipped-existing">
                  {result.skipped_existing.map((s, i) => (
                    <p key={i} className="text-xs text-stone font-mono">
                      Row {s.row}: <span className="text-charcoal">{s.sku}</span> · {s.mg}mg
                    </p>
                  ))}
                </div>
              </div>
            )}
            {result.skipped_unknown_sku?.length > 0 && (
              <div className="mt-3">
                <p className="text-[10px] uppercase tracking-widest font-mono text-stone mb-1">Skipped — unknown SKU</p>
                <div className="space-y-1 max-h-24 overflow-y-auto" data-testid="variants-skipped-unknown">
                  {result.skipped_unknown_sku.map((s, i) => (
                    <p key={i} className="text-xs text-stone font-mono">
                      Row {s.row}: <span className="text-oxblood">{s.sku}</span> · {s.mg}mg{s.name ? ` — "${s.name}"` : ""}
                    </p>
                  ))}
                </div>
              </div>
            )}
            {result.errors?.length > 0 && (
              <div className="mt-3">
                <p className="text-[10px] uppercase tracking-widest font-mono text-oxblood mb-1">Errors</p>
                <div className="space-y-1 max-h-24 overflow-y-auto" data-testid="variants-import-errors">
                  {result.errors.map((err, i) => (
                    <p key={i} className="text-xs text-stone font-mono">
                      Row {err.row}: {err.message}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
